import React from 'react';
import {
  Heart,
  Users,
  Award,
  Shield,
  Clock,
  MapPin,
  Stethoscope,
  Pill,
  TestTube,
  Video,
  Home,
  TrendingUp,
  CheckCircle,
  Target,
  Eye,
  Lightbulb
} from 'lucide-react';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';

const About = () => {
  const services = [
    {
      icon: <Stethoscope className="w-8 h-8" />,
      title: "Doctor Consultations",
      description: "Connect with experienced doctors for in-clinic appointments and online video consultations"
    },
    {
      icon: <Video className="w-8 h-8" />,
      title: "Online Consultations",
      description: "24/7 video and chat consultations with verified healthcare professionals from home"
    },
    {
      icon: <Pill className="w-8 h-8" />,
      title: "Medicine Delivery",
      description: "Order genuine medicines online with prescription upload and doorstep delivery"
    },
    {
      icon: <TestTube className="w-8 h-8" />,
      title: "Diagnostic Tests",
      description: "Book lab tests and health checkup packages with home sample collection"
    },
    {
      icon: <Home className="w-8 h-8" />,
      title: "Home Healthcare",
      description: "Professional paramedical services including nursing, physiotherapy, and elderly care at home"
    },
    {
      icon: <Heart className="w-8 h-8" />,
      title: "Health Packages",
      description: "Comprehensive health screening packages for complete wellness monitoring"
    }
  ];

  const values = [
    {
      icon: <Heart className="w-10 h-10" />,
      title: "Patient-Centric Care",
      description: "We put patients first, ensuring compassionate and personalized healthcare experiences"
    },
    {
      icon: <Shield className="w-10 h-10" />,
      title: "Quality Assurance",
      description: "Strict quality standards for all services, medicines, and healthcare professionals"
    },
    {
      icon: <Award className="w-10 h-10" />,
      title: "Trust & Transparency",
      description: "Clear pricing, verified professionals, and honest communication at every step"
    },
    {
      icon: <TrendingUp className="w-10 h-10" />,
      title: "Innovation",
      description: "Leveraging technology to make healthcare accessible, affordable, and efficient"
    }
  ];

  const stats = [
    { number: "10,000+", label: "Happy Patients" },
    { number: "500+", label: "Healthcare Professionals" },
    { number: "50+", label: "Partner Labs" },
    { number: "24/7", label: "Support Available" }
  ];

  const features = [
    "Verified & Experienced Healthcare Professionals",
    "Prescription Upload & Verification",
    "Home Sample Collection for Tests",
    "Secure Online Payments",
    "Electronic Health Records",
    "Medicine Authenticity Guarantee",
    "24/7 Emergency Support",
    "Free Follow-up Consultations"
  ];

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-blue-50 to-indigo-50">
        {/* Hero Section */}
        <div className="relative bg-gradient-to-r from-teal-600 via-blue-600 to-indigo-600 text-white overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute transform rotate-45 -left-20 -top-20 w-96 h-96 bg-white rounded-full"></div>
            <div className="absolute transform -rotate-12 right-0 top-40 w-72 h-72 bg-white rounded-full"></div>
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
            <div className="text-center">
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6">
                About HomeCure
              </h1>
              <p className="text-xl md:text-2xl text-teal-100 max-w-3xl mx-auto mb-4">
                A Brand of Sanghmitra Healthcare Private Limited
              </p>
              <p className="text-lg md:text-xl text-teal-100 max-w-3xl mx-auto">
                Bringing Quality Healthcare to Your Doorstep
              </p>
            </div>
          </div>
        </div>

        {/* Company Overview */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-blue-500 rounded-2xl flex items-center justify-center">
                <Heart className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">Who We Are</h2>
            </div>

            <div className="space-y-6 text-gray-700 text-lg leading-relaxed">
              <p>
                <span className="font-bold text-teal-600">Sanghmitra Healthcare Private Limited</span> is a pioneering healthcare company committed to transforming the way healthcare services are delivered in India. Operating under the brand name <span className="font-bold text-teal-600">HomeCure</span>, we bring comprehensive healthcare solutions directly to your home, making quality medical care accessible, affordable, and convenient.
              </p>

              <p>
                Founded with a vision to bridge the gap between patients and healthcare providers, HomeCure leverages cutting-edge technology and a vast network of healthcare professionals to deliver seamless medical services. From doctor consultations to medicine delivery, diagnostic tests to home healthcare services, we are your one-stop solution for all healthcare needs.
              </p>

              <p>
                Our platform connects patients with verified doctors, certified pathology labs, authentic pharmacies, and trained paramedical staff, ensuring that every service meets the highest standards of quality and safety. We understand that health is wealth, and we're committed to making healthcare accessible to everyone, everywhere.
              </p>
            </div>
          </div>
        </div>

        {/* Mission & Vision */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Mission */}
            <div className="bg-gradient-to-br from-teal-500 to-blue-600 rounded-3xl shadow-xl p-8 md:p-10 text-white">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                  <Target className="w-7 h-7" />
                </div>
                <h3 className="text-3xl font-bold">Our Mission</h3>
              </div>
              <p className="text-lg text-teal-50 leading-relaxed">
                To revolutionize healthcare delivery by providing comprehensive, technology-driven medical services that are accessible, affordable, and patient-centric. We aim to empower individuals to take control of their health journey with convenient access to quality healthcare at their doorstep.
              </p>
            </div>

            {/* Vision */}
            <div className="bg-gradient-to-br from-purple-500 to-pink-600 rounded-3xl shadow-xl p-8 md:p-10 text-white">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center">
                  <Eye className="w-7 h-7" />
                </div>
                <h3 className="text-3xl font-bold">Our Vision</h3>
              </div>
              <p className="text-lg text-purple-50 leading-relaxed">
                To become India's most trusted healthcare platform, where every individual has seamless access to quality medical care. We envision a future where technology and healthcare merge to create a healthier nation, one home at a time.
              </p>
            </div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          <div className="bg-gradient-to-r from-teal-600 to-blue-600 rounded-3xl shadow-xl p-8 md:p-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">
              Our Impact in Numbers
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-4xl md:text-5xl font-bold text-white mb-2">
                    {stat.number}
                  </div>
                  <div className="text-lg text-teal-100">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Services Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Services
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Comprehensive healthcare solutions delivered with care and convenience
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-shadow"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-blue-500 rounded-xl flex items-center justify-center text-white mb-4">
                  {service.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {service.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Core Values */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Core Values
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl shadow-lg p-8 text-center hover:shadow-xl transition-shadow"
              >
                <div className="w-20 h-20 bg-gradient-to-br from-teal-500 to-blue-500 rounded-full flex items-center justify-center text-white mx-auto mb-4">
                  {value.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {value.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Key Features */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center">
                <Lightbulb className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">Key Features</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {features.map((feature, index) => (
                <div key={index} className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0 mt-1" />
                  <span className="text-gray-700 text-lg">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Why Choose Us */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          <div className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-3xl shadow-xl p-8 md:p-12 text-white">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center">
              Why Choose HomeCure?
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold mb-3">Safety First</h3>
                <p className="text-indigo-100">All healthcare professionals are verified and certified</p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold mb-3">24/7 Availability</h3>
                <p className="text-indigo-100">Round-the-clock support for all your healthcare needs</p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MapPin className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold mb-3">Doorstep Service</h3>
                <p className="text-indigo-100">Convenient home delivery and sample collection</p>
              </div>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
          <div className="bg-gradient-to-r from-teal-600 to-blue-600 rounded-3xl shadow-xl p-8 md:p-12 text-center text-white">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Experience Healthcare at Your Convenience
            </h2>
            <p className="text-xl text-teal-100 mb-8 max-w-2xl mx-auto">
              Join thousands of satisfied patients who trust HomeCure for their healthcare needs
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/"
                className="inline-block bg-white text-teal-600 px-8 py-4 rounded-xl font-semibold text-lg hover:bg-teal-50 transition-colors"
              >
                Explore Services
              </a>
              <a
                href="/contact-us"
                className="inline-block bg-teal-500 text-white px-8 py-4 rounded-xl font-semibold text-lg hover:bg-teal-400 transition-colors border-2 border-white"
              >
                Contact Us
              </a>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default About;
